import {
  Box,
  Flex,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { SqlOperator } from "@src/__generated__/graphql";
import {
  TimeTrackingBillingStatusEnum,
  type TimeTrackingProjectReportOrderByColumnEnum,
  TimeTrackingReportsWhereColumn,
} from "@src/__generated__/urql-graphql";
import { OurWorkTooltipLabelRow } from "@src/components/modules/projects/detail/ProgressBar/OurWork";
import { formatCurrencyForTimeReport } from "@src/components/modules/projects/time-report/CardReport";
import type { TimeReportFilter } from "@src/components/modules/time-tracking/Report/time-report-url";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  ColoredAmount,
  Link,
} from "@src/components/ui-kit";
import { EditableText } from "@src/components/ui-kit/editable-text";
import { Icon } from "@src/components/ui-kit/Icon";
import {
  ProgressBar,
  type TProgressBarSegment,
} from "@src/components/ui-kit/NewProgressBar";
import { appStore } from "@src/stores/AppStore";
import type { WorkspaceStore } from "@src/stores/WorkspaceStore/WorkspaceStore";
import { can } from "@src/utils/components/permissions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import type { FieldState } from "formstate";
import { sumBy } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import type { LinkProps } from "next/link";
import type { ReactNode } from "react";

// Unified row type for both report and breakdown views
export interface TimeReportRow {
  id: string;

  // Common fields
  tracked_time: number;

  // Name field (different keys: label vs title)
  label?: string; // Report view
  title?: string; // Breakdown view
  image?: string | null; // Report view (avatar)

  // Amount fields (different keys)
  amount?: number; // Report view
  invoiced_amount?: number; // Report view
  tracked_amount?: number; // Breakdown view

  // Report-specific fields
  hourly_rate?: number;
  internal_hourly_rate?: number;
  firstLevelFilter?: TimeReportFilter;

  // Breakdown-specific fields
  budgeted?: number;
  budgeted_time?: number;
  progressBarSegments?: TProgressBarSegment[];
  canBeOpened?: boolean;
  showBudgetWarning?: boolean;
  budgetDifference?: number;

  // Accounting (different keys)
  personal_expense?: number; // Report view
  personal_expenses?: number; // Breakdown view
  personal_profitability?: number; // Report view
  invoiced_personal_profitability?: number;
  profit?: number; // Breakdown view

  // Invoicing (report view only)
  invoiced_seconds?: number;
  overserviced_seconds?: number;
  uninvoiced_seconds?: number;

  // Sub-rows
  __subRows?: TimeReportSubRow[];
}

export interface TimeReportSubRow {
  id: string;
  label?: string;
  title?: string;
  tracked_time: number;
  amount?: number;
  invoiced_amount?: number;
  tracked_amount?: number;
  budgeted?: number;
  budgeted_time?: number;
  personal_expense?: number;
  personal_expenses?: number;
  personal_profitability?: number;
  invoiced_personal_profitability?: number;
  profit?: number;
  progressBarSegments?: TProgressBarSegment[];
  budgetedField?: FieldState<string>;
  handleUpdateBudget?: (amount: number) => Promise<boolean>;
  secondLevelFilter?: TimeReportFilter;
  invoiced_seconds?: number;
  overserviced_seconds?: number;
  uninvoiced_seconds?: number;
}

export type TimeReportTableRow =
  | {
      id: string;
      rowType: "parent";
      group: TimeReportRow;
      __subRows?: TimeReportTableRow[];
    }
  | {
      id: string;
      rowType: "subRow";
      group: TimeReportRow;
      subRow: TimeReportSubRow;
    };

const isParentRow = (
  row: TimeReportTableRow,
): row is Extract<TimeReportTableRow, { rowType: "parent" }> =>
  row.rowType === "parent";

function renderHeaderWithTooltip(
  label: ReactNode,
  tooltip?: ReactNode,
): ReactNode {
  if (!tooltip) {
    return label;
  }

  return (
    <Tooltip label={tooltip}>
      <Box>{label}</Box>
    </Tooltip>
  );
}

// Helper components
const AmountCell = observer(function AmountCell({
  amount,
}: {
  amount?: number;
}) {
  return <span>{formatCurrencyForTimeReport(amount ?? 0)}</span>;
});

const ExpenseCell = observer(function ExpenseCell({
  expense,
  internalHourlyRate,
}: {
  expense?: number;
  internalHourlyRate?: number;
}) {
  return (
    <Tooltip
      label={
        <span>
          <Trans>Hourly rate</Trans>:{" "}
          {formatCurrencyForTimeReport(internalHourlyRate ?? 0)}
        </span>
      }
    >
      <span>{formatCurrencyForTimeReport(expense ?? 0)}</span>
    </Tooltip>
  );
});

const ProfitabilityCell = observer(function ProfitabilityCell({
  profitability,
}: {
  profitability?: number;
}) {
  return (
    <ColoredAmount value={profitability ?? 0}>
      {formatCurrencyForTimeReport(profitability ?? 0)}
    </ColoredAmount>
  );
});

const ItemProgress = observer(function ItemProgress({
  trackedTime,
  budgetedTime,
  trackedAmount,
  budgetedAmount,
  segments,
}: {
  trackedTime: number;
  budgetedTime: number;
  trackedAmount: number;
  budgetedAmount: number;
  segments: TProgressBarSegment[];
}) {
  return (
    <Popover placement="bottom-start" trigger="hover">
      <PopoverTrigger>
        <Box>
          <ProgressBar segments={segments} size="sm" />
        </Box>
      </PopoverTrigger>
      <PopoverContent color="whiteAlpha.900" bg="grey.800" borderRadius="xl">
        <Box py="5">
          <OurWorkTooltipLabelRow
            title={t`Budget`}
            time={budgetedTime}
            budget={budgetedAmount}
            hideBadge
          />
        </Box>
        <Box py="5">
          <OurWorkTooltipLabelRow
            title={t`Tracked`}
            time={trackedTime}
            budget={trackedAmount}
            bg="teal.300"
            hideBadge
          />
        </Box>
      </PopoverContent>
    </Popover>
  );
});

const BudgetCell = observer(function BudgetCell({
  budgeted,
  showBudgetWarning,
  budgetDifference,
}: {
  budgeted?: number;
  showBudgetWarning?: boolean;
  budgetDifference?: number;
}) {
  return (
    <Text color={showBudgetWarning ? "orange" : undefined} textAlign="right">
      <WrapComponent
        if={showBudgetWarning ?? false}
        with={(children) => (
          <Tooltip
            label={
              <Box>
                {t`Monthly breakdown must match the amount from assigned budget.`}
                <br />
                <br />
                <Text>
                  {Math.sign(budgetDifference ?? 0) === 1
                    ? t`Missing:`
                    : t`Over budget by:`}
                  &nbsp;
                  {formatCurrencyForTimeReport(Math.abs(budgetDifference ?? 0))}
                </Text>
              </Box>
            }
            shouldWrapChildren
          >
            <Icon name="alert-triangle" />
            &nbsp;
            {children}
          </Tooltip>
        )}
      >
        {formatCurrencyForTimeReport(budgeted ?? 0)}
      </WrapComponent>
    </Text>
  );
});

const EditableBudgetCell = observer(function EditableBudgetCell({
  budgeted,
  budgetedField,
  fetchMonthlyBreakdown,
  handleUpdateBudget,
}: {
  budgeted?: number;
  budgetedField?: FieldState<string>;
  fetchMonthlyBreakdown?: () => Promise<void>;
  handleUpdateBudget?: (amount: number) => Promise<boolean>;
}) {
  const { isLoading } = useLocalObservable(() => ({
    isLoading: new BooleanState(),
  }));

  if (!budgetedField || !fetchMonthlyBreakdown || !handleUpdateBudget) {
    return (
      <Text textAlign="right">
        {formatCurrencyForTimeReport(budgeted ?? 0)}
      </Text>
    );
  }

  return (
    <HStack justify="flex-end" direction="row">
      <Box>
        <EditableText
          isEditable={can("project_accessAccountingAttributes")}
          field={budgetedField}
          isLoading={isLoading.value}
          inputStyles={{
            textAlign: "right",
            maxW: "100px",
          }}
          onConfirm={async () => {
            isLoading.on();
            const validation = await budgetedField.validate();

            if (validation.hasError) {
              isLoading.off();
              return;
            }

            const success = await handleUpdateBudget(Number(validation.value));
            isLoading.off();
            if (!success) return;
            fetchMonthlyBreakdown();
          }}
          customContent={
            <Text>{formatCurrencyForTimeReport(budgeted ?? 0)}</Text>
          }
        />
      </Box>
    </HStack>
  );
});

interface CreateTimeReportColumnsOptions {
  isBreakdown: boolean;
  workspaceStore: WorkspaceStore;
  getUrl?: (add?: TimeReportFilter[]) => LinkProps["href"];
  fetchMonthlyBreakdown?: () => Promise<void>;
}

export function createTimeReportColumns(
  options: CreateTimeReportColumnsOptions,
): BodyColumnDef<
  TimeReportTableRow,
  TimeTrackingProjectReportOrderByColumnEnum
>[] {
  const { isBreakdown, workspaceStore, getUrl, fetchMonthlyBreakdown } =
    options;
  const columns: BodyColumnDef<
    TimeReportTableRow,
    TimeTrackingProjectReportOrderByColumnEnum
  >[] = [
    {
      id: "name",
      meta: { label: t`Name` },
      header: () => <Trans>Name</Trans>,
      cell: ({ row }) =>
        isParentRow(row.original) ? (
          isBreakdown ? (
            <Text>{row.original.group.title}</Text>
          ) : (
            <Flex align="center" gap="2">
              {row.original.group.image && (
                <Avatar src={row.original.group.image} />
              )}
              <span>{row.original.group.label}</span>
            </Flex>
          )
        ) : (
          <span>{row.original.subRow.label ?? row.original.subRow.title}</span>
        ),
    },
  ];

  if (isBreakdown) {
    columns.push({
      id: "progress",
      meta: { label: "progress", isReorderDisabled: true },
      header: "",
      enableHiding: false,
      cell: ({ row }) =>
        (
          isParentRow(row.original)
            ? row.original.group.progressBarSegments
            : row.original.subRow.progressBarSegments
        ) ? (
          <ItemProgress
            trackedTime={
              isParentRow(row.original)
                ? row.original.group.tracked_time
                : row.original.subRow.tracked_time
            }
            budgetedTime={
              isParentRow(row.original)
                ? (row.original.group.budgeted_time ?? 0)
                : (row.original.subRow.budgeted_time ?? 0)
            }
            trackedAmount={
              isParentRow(row.original)
                ? (row.original.group.tracked_amount ?? 0)
                : (row.original.subRow.tracked_amount ?? 0)
            }
            budgetedAmount={
              isParentRow(row.original)
                ? (row.original.group.budgeted ?? 0)
                : (row.original.subRow.budgeted ?? 0)
            }
            segments={
              isParentRow(row.original)
                ? (row.original.group.progressBarSegments ?? [])
                : (row.original.subRow.progressBarSegments ?? [])
            }
          />
        ) : null,
    });
  }

  columns.push({
    id: "tracked_time",
    meta: {
      label: workspaceStore.settings?.timeTrackingRateTypeReadable() ?? "",
      align: "right",
    },
    header: () => workspaceStore.settings?.timeTrackingRateTypeReadable(),
    cell: ({ row }) =>
      isBreakdown ? (
        <Text textAlign="right">
          {isParentRow(row.original)
            ? row.original.group.tracked_time
            : row.original.subRow.tracked_time}
        </Text>
      ) : (
        renderTimeLink(
          isParentRow(row.original)
            ? row.original.group.tracked_time
            : row.original.subRow.tracked_time,
          row.original.group.firstLevelFilter,
          isParentRow(row.original)
            ? undefined
            : row.original.subRow.secondLevelFilter,
          getUrl,
          workspaceStore,
        )
      ),
  });

  if (
    !isBreakdown &&
    appStore.workspaceStore.settings?.moduleSettings.time_billing
  ) {
    columns.push(
      {
        id: "invoiced_seconds",
        meta: { label: t`Invoiced`, align: "right" },
        header: () =>
          renderHeaderWithTooltip(
            <Trans>Invoiced</Trans>,
            <Trans>Hours that have been invoiced</Trans>,
          ),
        cell: ({ row }) =>
          renderTimeLink(
            isParentRow(row.original)
              ? (row.original.group.invoiced_seconds ?? 0)
              : (row.original.subRow.invoiced_seconds ?? 0),
            row.original.group.firstLevelFilter,
            isParentRow(row.original)
              ? undefined
              : row.original.subRow.secondLevelFilter,
            getUrl,
            workspaceStore,
            [
              {
                column: TimeTrackingReportsWhereColumn.BillingStatus,
                operator: SqlOperator.In,
                value: [TimeTrackingBillingStatusEnum.Invoiced],
              },
            ],
          ),
      },
      {
        id: "overserviced_seconds",
        meta: { label: t`Overserviced`, align: "right" },
        header: () =>
          renderHeaderWithTooltip(
            <Trans>Overserviced</Trans>,
            <Trans>Hours exceeding what was invoiced</Trans>,
          ),
        cell: ({ row }) =>
          renderTimeLink(
            isParentRow(row.original)
              ? (row.original.group.overserviced_seconds ?? 0)
              : (row.original.subRow.overserviced_seconds ?? 0),
            row.original.group.firstLevelFilter,
            isParentRow(row.original)
              ? undefined
              : row.original.subRow.secondLevelFilter,
            getUrl,
            workspaceStore,
            [
              {
                column: TimeTrackingReportsWhereColumn.BillingStatus,
                operator: SqlOperator.In,
                value: [TimeTrackingBillingStatusEnum.Overserviced],
              },
            ],
          ),
      },
      {
        id: "uninvoiced_seconds",
        meta: { label: t`Uninvoiced`, align: "right" },
        header: () =>
          renderHeaderWithTooltip(
            <Trans>Uninvoiced</Trans>,
            <Trans>Hours that have not been invoiced</Trans>,
          ),
        cell: ({ row }) =>
          renderTimeLink(
            isParentRow(row.original)
              ? (row.original.group.uninvoiced_seconds ?? 0)
              : (row.original.subRow.uninvoiced_seconds ?? 0),
            row.original.group.firstLevelFilter,
            isParentRow(row.original)
              ? undefined
              : row.original.subRow.secondLevelFilter,
            getUrl,
            workspaceStore,
            [
              {
                column: TimeTrackingReportsWhereColumn.BillingStatus,
                operator: SqlOperator.In,
                value: [TimeTrackingBillingStatusEnum.Uninvoiced],
              },
            ],
          ),
      },
    );
  }

  columns.push({
    id: "amount",
    meta: { label: t`Amount`, align: "right" },
    header: () =>
      renderHeaderWithTooltip(
        <Trans>Amount</Trans>,
        <Trans>Calculated based on tracked hours</Trans>,
      ),
    cell: ({ row }) => (
      <AmountCell
        amount={
          isBreakdown
            ? isParentRow(row.original)
              ? row.original.group.tracked_amount
              : row.original.subRow.tracked_amount
            : appStore.workspaceStore.settings?.moduleSettings.time_billing
              ? isParentRow(row.original)
                ? row.original.group.invoiced_amount
                : row.original.subRow.invoiced_amount
              : isParentRow(row.original)
                ? row.original.group.amount
                : row.original.subRow.amount
        }
      />
    ),
  });

  if (isBreakdown) {
    columns.push({
      id: "budgeted",
      meta: { label: t`Budget`, align: "right" },
      header: () => <Trans>Budget</Trans>,
      size: 220,
      cell: ({ row }) =>
        isParentRow(row.original) ? (
          <BudgetCell
            budgeted={row.original.group.budgeted}
            showBudgetWarning={row.original.group.showBudgetWarning}
            budgetDifference={row.original.group.budgetDifference}
          />
        ) : (
          <EditableBudgetCell
            budgeted={row.original.subRow.budgeted}
            budgetedField={row.original.subRow.budgetedField}
            fetchMonthlyBreakdown={fetchMonthlyBreakdown}
            handleUpdateBudget={row.original.subRow.handleUpdateBudget}
          />
        ),
    });
  }

  if (can("user_accessAccountingAttributes")) {
    columns.push(
      {
        id: "personal_expense",
        meta: {
          label: isBreakdown
            ? t`Personal expenses`
            : t`Personal expense per user`,
          align: "right",
        },
        header: () =>
          renderHeaderWithTooltip(
            isBreakdown ? (
              <Trans>Personal expenses</Trans>
            ) : (
              <Trans>Personal expense per user</Trans>
            ),
            <Trans>Calculated based on tracked hours</Trans>,
          ),
        cell: ({ row }) => (
          <ExpenseCell
            expense={
              isBreakdown
                ? isParentRow(row.original)
                  ? row.original.group.personal_expenses
                  : row.original.subRow.personal_expenses
                : isParentRow(row.original)
                  ? row.original.group.personal_expense
                  : row.original.subRow.personal_expense
            }
            internalHourlyRate={
              isParentRow(row.original)
                ? row.original.group.internal_hourly_rate
                : 0
            }
          />
        ),
      },
      {
        id: "profitability",
        meta: {
          label: isBreakdown ? t`Profit` : t`User profitability`,
          align: "right",
        },
        header: () =>
          renderHeaderWithTooltip(
            isBreakdown ? (
              <Trans>Profit</Trans>
            ) : (
              <Trans>User profitability</Trans>
            ),
            isBreakdown ? (
              <Trans>Amount - Personal expenses</Trans>
            ) : (
              <Trans>Amount - Personal expense per user</Trans>
            ),
          ),
        cell: ({ row }) => (
          <ProfitabilityCell
            profitability={
              isBreakdown
                ? isParentRow(row.original)
                  ? row.original.group.profit
                  : row.original.subRow.profit
                : appStore.workspaceStore.settings?.moduleSettings.time_billing
                  ? isParentRow(row.original)
                    ? row.original.group.invoiced_personal_profitability
                    : row.original.subRow.invoiced_personal_profitability
                  : isParentRow(row.original)
                    ? row.original.group.personal_profitability
                    : row.original.subRow.personal_profitability
            }
          />
        ),
      },
    );
  }

  columns.push({
    id: ACTIONS_COLUMN_ID,
    meta: { label: "actions", isReorderDisabled: true },
    cell: () => <Box />,
  });

  return columns;
}

// Helper function to render time links
function renderTimeLink(
  time: number,
  firstLevelFilter?: TimeReportFilter,
  secondLevelFilter?: TimeReportFilter,
  getUrl?: (add?: TimeReportFilter[]) => LinkProps["href"],
  workspaceStore?: WorkspaceStore,
  extraFilters?: TimeReportFilter[],
) {
  const filters =
    firstLevelFilter && secondLevelFilter
      ? [firstLevelFilter, secondLevelFilter]
      : firstLevelFilter
        ? [firstLevelFilter]
        : [];

  if (extraFilters) {
    filters.push(...extraFilters);
  }

  const url = getUrl?.(filters);
  return url ? (
    <Link href={url}>
      {workspaceStore?.settings!.convertTimeTrackingRate(time)}
    </Link>
  ) : (
    <span>{workspaceStore?.settings!.convertTimeTrackingRate(time)}</span>
  );
}

// Footer totals types and factory
interface CreateFooterTotalsOptions {
  isBreakdown: boolean;
  reportTotalAmount: number;
  reportTotalProfitability: number;
  breakdownTotalTrackedTime?: number;
  breakdownTotalAmount?: number;
  breakdownTotalBudget?: number;
  breakdownTotalPersonalExpense?: number;
  breakdownTotalProfit?: number;
  reportRows: TimeReportRow[];
  getUrl?: (add?: TimeReportFilter[]) => LinkProps["href"];
}
export type TimeReportFooterMap = Partial<Record<string, () => ReactNode>>;

export function createTimeReportFooterTotals(
  options: CreateFooterTotalsOptions,
): TimeReportFooterMap {
  const { workspaceStore } = appStore;
  const {
    isBreakdown,
    reportTotalAmount,
    reportTotalProfitability,
    breakdownTotalTrackedTime,
    breakdownTotalAmount,
    breakdownTotalBudget,
    breakdownTotalPersonalExpense,
    breakdownTotalProfit,
    reportRows,
    getUrl,
  } = options;

  const totals: TimeReportFooterMap = {
    name: () => (
      <Text fontWeight="bold">
        <Trans>Total</Trans>
      </Text>
    ),
  };

  if (isBreakdown) {
    totals.progress = () => null;
  }

  totals.tracked_time = () => (
    <Text fontWeight="bold" textAlign="right">
      {isBreakdown ? (
        breakdownTotalTrackedTime
      ) : getUrl ? (
        <Link href={getUrl()}>
          {workspaceStore.settings!.convertTimeTrackingRate(
            sumBy(reportRows, "tracked_time"),
          )}
        </Link>
      ) : (
        workspaceStore.settings!.convertTimeTrackingRate(
          sumBy(reportRows, "tracked_time"),
        )
      )}
    </Text>
  );

  if (!isBreakdown && workspaceStore.settings?.moduleSettings.time_billing) {
    totals.invoiced_seconds = () => (
      <Text fontWeight="bold" textAlign="right">
        {renderTimeLink(
          sumBy(reportRows, (r: TimeReportRow) => r.invoiced_seconds ?? 0),
          undefined,
          undefined,
          getUrl,
          workspaceStore,
          [
            {
              column: TimeTrackingReportsWhereColumn.BillingStatus,
              operator: SqlOperator.In,
              value: [TimeTrackingBillingStatusEnum.Invoiced],
            },
          ],
        )}
      </Text>
    );
    totals.overserviced_seconds = () => (
      <Text fontWeight="bold" textAlign="right">
        {renderTimeLink(
          sumBy(reportRows, (r: TimeReportRow) => r.overserviced_seconds ?? 0),
          undefined,
          undefined,
          getUrl,
          workspaceStore,
          [
            {
              column: TimeTrackingReportsWhereColumn.BillingStatus,
              operator: SqlOperator.In,
              value: [TimeTrackingBillingStatusEnum.Overserviced],
            },
          ],
        )}
      </Text>
    );
    totals.uninvoiced_seconds = () => (
      <Text fontWeight="bold" textAlign="right">
        {renderTimeLink(
          sumBy(reportRows, (r: TimeReportRow) => r.uninvoiced_seconds ?? 0),
          undefined,
          undefined,
          getUrl,
          workspaceStore,
          [
            {
              column: TimeTrackingReportsWhereColumn.BillingStatus,
              operator: SqlOperator.In,
              value: [TimeTrackingBillingStatusEnum.Uninvoiced],
            },
          ],
        )}
      </Text>
    );
  }

  totals.amount = () => (
    <Text fontWeight="bold" textAlign="right">
      {formatCurrencyForTimeReport(
        isBreakdown ? (breakdownTotalAmount ?? 0) : reportTotalAmount,
      )}
    </Text>
  );

  if (isBreakdown) {
    totals.budgeted = () => (
      <Text fontWeight="bold" textAlign="right">
        {formatCurrencyForTimeReport(breakdownTotalBudget ?? 0)}
      </Text>
    );
  }

  if (can("user_accessAccountingAttributes")) {
    totals.personal_expense = () => (
      <Text fontWeight="bold" textAlign="right">
        {formatCurrencyForTimeReport(
          isBreakdown
            ? (breakdownTotalPersonalExpense ?? 0)
            : sumBy(reportRows, (r: TimeReportRow) => r.personal_expense ?? 0),
        )}
      </Text>
    );
    totals.profitability = () => (
      <Text fontWeight="bold" textAlign="right">
        <ColoredAmount
          value={
            isBreakdown ? (breakdownTotalProfit ?? 0) : reportTotalProfitability
          }
        >
          {formatCurrencyForTimeReport(
            isBreakdown
              ? (breakdownTotalProfit ?? 0)
              : reportTotalProfitability,
          )}
        </ColoredAmount>
      </Text>
    );
  }

  totals.actions = () => <Box />;

  return totals;
}
